import { motion } from "framer-motion";
import { ShoppingBag, MessageCircle, Send, ArrowUpRight, CheckCircle2, AlertTriangle, Instagram, Zap } from "lucide-react";

// --- CONFIGURAZIONE LINK E STATO ---
const IS_PAYMENT_LIVE = true; // Imposta a 'false' se vuoi mostrare la data di lancio
const TELEGRAM_LINK = "https://t.me/zerosignals_hub";
const INSTAGRAM_LINK = "https://www.instagram.com/zerosignals_hub?igsi=eHV6ZTE1OXU0ZDE3"; 
const LAUNCH_DATE = "5 Ottobre 2026";

// STRIPE PAYMENT LINKS DIRETTI
const STRIPE_LINKS = {
  basic: "https://buy.stripe.com/4gM8wRedK5Ah70E0Go2cg00",
  premium: "https://buy.stripe.com/3cIcN7fhO2o51Gkdta2cg01",
  diamond: "https://buy.stripe.com/6oUcN7d9Ge6NgBe88Q2cg02",
};
// ----------------------------------

const PRICING_TIERS = [
  {
    name: "Basic",
    price: "€10,99",
    period: " / 30 giorni",
    desc: "Per iniziare a utilizzare ZeroSignals.",
    badge: null,
    key: "basic",
  },
  {
    name: "Premium",
    price: "€17,99",
    period: " / 3 mesi",
    desc: "Più tempo per integrare ZeroSignals nelle tue analisi.",
    badge: "Popular",
    key: "premium",
  },
  {
    name: "Diamond",
    price: "€59,99",
    period: " / 1 anno",
    desc: "Accesso annuale al miglior costo mensile.",
    badge: "Best Value",
    key: "diamond",
  },
];

export default function TheHub() {
  return (
    <section
      id="the-hub"
      data-testid="the-hub-section"
      className="relative border-t border-brand-gold/20 py-28 lg:py-40"
    >
      {/* ambient glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-1/2 h-[480px] w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.10),transparent_60%)] blur-3xl" />
        <div className="absolute left-[20%] top-[15%] h-[260px] w-[260px] rounded-full bg-[radial-gradient(circle,rgba(0,240,255,0.10),transparent_60%)] blur-3xl" />
        <div className="absolute right-[15%] bottom-[10%] h-[260px] w-[260px] rounded-full bg-[radial-gradient(circle,rgba(176,38,255,0.12),transparent_60%)] blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-3xl text-center"
        >
          <div className="mx-auto mb-6 inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.28em] text-brand-neon-blue">
            <span className="h-px w-4 bg-brand-gold" />
            Entra nell&apos;Ecosistema
            <span className="h-px w-4 bg-brand-gold" />
          </div>
          <h2 className="font-display text-4xl font-black leading-[1.15] tracking-tighter md:text-6xl text-white">
            Un Unico <span className="text-gradient-gold">Hub.</span> <br />
            <span className="text-glow-blue">Zero rumore.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/65 sm:text-lg">
            Dimentica le pagine frammentate e le notifiche disperse. Tutto ciò di cui hai bisogno vive all&apos;interno di un unico ecosistema premium.
          </p>

          {/* BADGE STATO */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className={`mt-8 inline-flex items-center gap-2.5 rounded-full border px-4 py-2 text-xs font-bold tracking-widest uppercase backdrop-blur-md ${
              IS_PAYMENT_LIVE
                ? "border-green-500/30 bg-green-500/10 text-green-400"
                : "border-red-500/30 bg-red-500/10 text-red-400"
            }`}
          >
            {IS_PAYMENT_LIVE ? (
              <CheckCircle2 size={14} />
            ) : (
              <AlertTriangle size={14} className="animate-pulse" />
            )}
            <span>
              {IS_PAYMENT_LIVE ? "Pagamenti Attivi" : `Pagamenti disponibili dal ${LAUNCH_DATE}`}
            </span>
          </motion.div>
        </motion.div>

        {/* PRICING TIERS CON PULSANTI DI PAGAMENTO IDENTICI */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-14 rounded-2xl border border-white/10 bg-gradient-to-b from-[#121212] to-[#0A0A0A] overflow-hidden"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/5">
            {PRICING_TIERS.map((tier, idx) => (
              <div
                key={tier.name}
                className="p-6 lg:p-8 relative flex flex-col justify-between transition-colors bg-brand-gold/[0.02]"
              >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/50 to-transparent" />

                <div>
                  <div className="h-6 flex items-center justify-between gap-2 mb-4">
                    <span className="text-xs uppercase font-bold tracking-widest text-white/40">{tier.name}</span>
                    {tier.badge && (
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${
                        idx === 2
                          ? "bg-brand-neon-blue/10 border-brand-neon-blue/30 text-brand-neon-blue"
                          : "bg-brand-gold/10 border-brand-gold/30 text-brand-gold"
                      }`}>
                        {tier.badge}
                      </span>
                    )}
                  </div>
                 
                  <div className="text-3xl font-black font-display text-white mb-2">
                    {tier.price}
                    <span className="text-xs font-normal text-white/40">{tier.period}</span>
                  </div>
                  <p className="text-xs text-white/60 leading-relaxed mb-6">{tier.desc}</p>
                </div>

                <div className="mt-auto pt-4">
                  <a
                    href={IS_PAYMENT_LIVE ? STRIPE_LINKS[tier.key] : TELEGRAM_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-xs font-bold uppercase tracking-[0.16em] transition-all bg-brand-gold text-black hover:bg-brand-gold-hover hover:shadow-[0_0_25px_-5px_rgba(212,175,55,0.6)]"
                  >
                    <ShoppingBag size={14} strokeWidth={2} />
                    {IS_PAYMENT_LIVE ? "Acquista Ora" : "Richiedi Info"}
                    <ArrowUpRight size={14} strokeWidth={2} />
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* ZERO PASS 24H — Flash CTA full-width */}
          <a
            href={IS_PAYMENT_LIVE ? "https://buy.stripe.com/bJedRb8Tq1k198M1Ks2cg03" : TELEGRAM_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="zeropass-24h-cta"
            className="group relative block border-t border-white/5 bg-gradient-to-r from-brand-neon-blue/[0.06] via-brand-neon-blue/[0.03] to-transparent transition-all hover:from-brand-neon-blue/[0.12] hover:via-brand-neon-blue/[0.06]"
          >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-neon-blue/60 to-transparent" />
            <div className="flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between lg:px-8">
              <div className="flex items-center gap-4">
                <span className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-brand-neon-blue/40 bg-brand-neon-blue/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-neon-blue">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-neon-blue animate-pulse" />
                  Flash 24H
                </span>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-display text-base font-bold text-white">
                      Zero Pass — 24H
                    </span>
                    <span className="font-display text-lg font-black text-gradient-gold">
                      €2,99
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-white/55 leading-snug">
                    Accesso completo per 24 ore. Prova ZeroSignals senza impegno.
                  </p>
                </div>
              </div>

              <div className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl border border-brand-neon-blue/60 bg-black px-5 py-3 text-xs font-bold uppercase tracking-[0.16em] text-brand-neon-blue transition-all group-hover:bg-brand-neon-blue group-hover:text-black group-hover:shadow-[0_0_28px_-4px_rgba(0,240,255,0.7)]">
                <Zap size={14} strokeWidth={2} />
                {IS_PAYMENT_LIVE ? "Attiva Zero Pass" : "Richiedi Info"}
                <ArrowUpRight size={14} strokeWidth={2} />
              </div>
            </div>
          </a>

          <div className="border-t border-white/5 bg-black/40 px-6 py-4 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[11px] text-white/50 uppercase tracking-wider">
            <div className="flex items-center gap-1.5"><CheckCircle2 size={13} className="text-brand-gold" /> Update istantanei</div>
            <div className="flex items-center gap-1.5"><CheckCircle2 size={13} className="text-brand-gold" /> Supporto Dedicato</div>
            <div className="flex items-center gap-1.5"><CheckCircle2 size={13} className="text-brand-gold" /> Nessun costo extra</div>
          </div>
        </motion.div>

        {/* CARDS SOCIAL (TELEGRAM & INSTAGRAM) */}
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
          {/* CARD TELEGRAM */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#141414] to-[#0E0E0E] p-8 lg:p-10"
            data-testid="hub-telegram-card"
          >
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-brand-neon-blue/10 blur-3xl transition-all duration-500 group-hover:bg-brand-neon-blue/20" />
            <div className="relative">
              <div className="grid h-14 w-14 place-items-center rounded-xl border border-brand-neon-blue/40 bg-brand-neon-blue/[0.08] text-brand-neon-blue">
                <MessageCircle size={22} strokeWidth={1.6} />
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.24em] text-white/40">
                Supporto · Linea Diretta
              </div>
              <h3 className="mt-5 font-display text-2xl font-bold leading-tight tracking-tight text-white md:text-3xl">
                Parlaci su <span className="text-glow-blue">Telegram</span>
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-white/60">
                Nessun ticket, nessuna coda infinita e nessun bot standardizzato. Interagisci direttamente con noi per ricevere risposte rapide.
              </p>

              <a
                href={TELEGRAM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hub-telegram-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.16em] text-white transition-all hover:bg-white/10 hover:border-brand-neon-blue"
              >
                <Send size={15} strokeWidth={2} />
                Unisciti a Telegram
                <ArrowUpRight size={15} strokeWidth={2} />
              </a>
            </div>
          </motion.div>

          {/* CARD INSTAGRAM */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#141414] to-[#0E0E0E] p-8 lg:p-10"
            data-testid="hub-instagram-card"
          >
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-pink-500/10 blur-3xl transition-all duration-500 group-hover:bg-pink-500/20" />
            <div className="relative">
              <div className="grid h-14 w-14 place-items-center rounded-xl border border-pink-500/40 bg-pink-500/[0.08] text-pink-400">
                <Instagram size={22} strokeWidth={1.6} />
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.24em] text-white/40">
                Community · Social
              </div>
              <h3 className="mt-5 font-display text-2xl font-bold leading-tight tracking-tight text-white md:text-3xl">
                Seguici su <span className="bg-gradient-to-r from-amber-400 via-pink-500 to-purple-500 bg-clip-text text-transparent">Instagram</span>
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-white/60">
                Contenuti, aggiornamenti e approfondimenti ZeroSignals: retroscena della piattaforma, novità e storie della community direttamente sul nostro profilo ufficiale.
              </p>

              <a
                href={INSTAGRAM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hub-instagram-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.16em] text-white transition-all hover:bg-white/10 hover:border-pink-500"
              >
                <Instagram size={15} strokeWidth={2} />
                Segui su Instagram
                <ArrowUpRight size={15} strokeWidth={2} />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}